import {getQuery, postQuery, putQuery, removeQuery} from "@/modules/common/utils";
import { httpGatewayService } from "@/modules/common/services";

const NEW_INVOICE_PATH = 'invoices/new_invoice';
const NEW_ESTIMATE_PATH = 'estimates/new_estimate';

const newInvoice = (client_uid, matter_uid, invoice_uid, from_estimate_uid) => getQuery(`${NEW_INVOICE_PATH}`, {client_uid, matter_uid, invoice_uid, from_estimate_uid});
const newEstimate = (client_uid, matter_uid, estimate_uid) => getQuery(`${NEW_ESTIMATE_PATH}`, {client_uid, matter_uid, estimate_uid});

const transformDiscount = (discount) => {
  if (!discount) return null;
  if (discount.percent !== undefined) {
    return { discount_type: 'percent', amount: discount.percent };
  }
  if (discount.amount !== undefined) {
    return { discount_type: 'amount', amount: discount.amount };
  }
  return null;
};

const transformLineItems = (items) => {
  return items.map(item => ({
    name: item.name,
    quantity: item.quantity,
    unit_amount: item.unit_amount ?? item.rate,
    description: item.description,
    entity_uid: item.entity_uid,
    entity_type: item.entity_type,
    tax_uids: item.tax_uids,
    discount: transformDiscount(item.discount),
    line_item_index: item.item_index,
    ...(item.uid && { uid: item.uid })
  }));
};

/**
 * Transform Core API invoice format to einvoicing API format
 * Core API: { invoice_label, invoice_number, status, items, sections, etc. }
 * einvoicing API: { document_title, unique_number, status, line_items, item_headers, etc. }
 */
const transformCoreToEinvoicing = (coreInvoice) => {
  // Transform status: map PAID/PARTIALLY_PAID to ISSUED, otherwise keep uppercase
  const statusUpper = coreInvoice.status?.toUpperCase();
  const status = statusUpper && ['PAID', 'PARTIALLY_PAID'].includes(statusUpper) ? 'ISSUED' : statusUpper;

  const lineItemGroups = (coreInvoice.sections || []).map(section => ({
    name: section.name,
    line_item_group_index: section.section_index,
    line_items: transformLineItems(section.items || [])
  }));

  const lineItems = transformLineItems(coreInvoice.items || []);

  const transformed = {
    matter_uid: coreInvoice.matter_uid,
    issue_date: coreInvoice.issue_date,
    due_date: coreInvoice.due_date,
    currency: coreInvoice.currency,
    status,
    unique_number: coreInvoice.invoice_number,
    invoice_title: coreInvoice.invoice_label,
    billing_address: coreInvoice.billing_address,
    purchase_order: coreInvoice.purchase_order,
    allow_online_payment: coreInvoice.allow_online_payment,
    allow_partial_payment: coreInvoice.allow_partial_payment,
    note: coreInvoice.note,
    terms_and_conditions: coreInvoice.terms_and_conditions,
    additional_recipients: coreInvoice.additional_recipients,
    display_line_item_groups_total: coreInvoice.display_sections_total,
    display_line_item_total: coreInvoice.display_items_total,
    enable_late_fee: coreInvoice.enable_late_fee,
    estimate_uid: coreInvoice.from_estimate_uid,
    notify_recipient: coreInvoice.notify_recipient,
    source_name: coreInvoice.source_name,
  };

  if (lineItemGroups.length > 0) {
    transformed.line_item_groups = lineItemGroups;
  }
  if (lineItems.length > 0) {
    transformed.line_items = lineItems;
  }

  return transformed;
};

/**
 * Handle einvoicing API errors consistently
 * Transforms einvoicing error responses to a common format
 */
const handleEinvoicingError = ({response, message}) => {
  if (!response) {
    throw {error: message}
  } else if (response.data?.errors?.length > 0) {
    const error = response.data.errors[0];
    throw {
      error: error.message, 
      status: response.status, 
      code: error.code, 
      field: error.field || 'general'
    }
  }
  throw {error: message || 'Unknown error'};
};

const createMultiActivityInvoice = (params, einvoicing = false) => {
  if (einvoicing) {
    // Transform from Core API format to einvoicing API format before sending
    const einvoicingParams = transformCoreToEinvoicing(params.invoice);
    
    return httpGatewayService.post(`/v3/payments/invoices`, einvoicingParams)
      .then(res => res.data) // Return invoice object directly
      .catch(handleEinvoicingError);
  } else {
    params.new_api = true;
    return postQuery('/business/payments/v1/invoices',params).then(res => res.data.invoice);
  }
}

const updateMultiActivityInvoice = (params, invoice_uid, einvoicing = false) => {
  if (einvoicing) {
    // Transform from Core API format to einvoicing API format before sending
    const einvoicingParams = transformCoreToEinvoicing(params.invoice);
    
    return httpGatewayService.put(`/v3/payments/invoices/${invoice_uid}`, einvoicingParams)
      .then(res => res.data) // Return invoice object directly
      .catch(handleEinvoicingError);
  } else {
    params.new_api = true;
    return putQuery(`/business/payments/v1/invoices/${invoice_uid}`, params).then(res => res.data.invoice);
  }
}

const createEstimate = (params) => {
  params.new_api = true;
  return postQuery('/business/payments/v1/estimates',params)
}

const updateEstimate = (params, estimate_uid) => {
  params.new_api = true;
  return putQuery(`/business/payments/v1/estimates/${estimate_uid}`, params)
}

const createDeposit = (params) => {
  params.new_api = true;
  return postQuery(`/business/payments/v1/deposits`, params);
}

const getDeposit = (params) => {
  return getQuery(`/business/payments/v1/deposits`, {
    new_api: true,
    "filter[entity_uid][eq]": params.entity_uid,
    "filter[entity_type][in]": params.entity_type
  });
}

const updateDeposit = (params) => {
  params.new_api = true;
  return putQuery(`/business/payments/v1/deposits/${params.deposit.uid}`, params);
}

const deleteDeposit = (depositUid) => {
  const params = {new_api: true};
  return removeQuery(`/business/payments/v1/deposits/${depositUid}`, params);
}

export const itemizableService = {
  getDeposit,
  newInvoice,
  newEstimate,
  updateDeposit,
  deleteDeposit,
  createDeposit,
  createEstimate,
  updateEstimate,
  createMultiActivityInvoice,
  updateMultiActivityInvoice
}
